'use client';

import { useCallback } from 'react';
import styled from 'styled-components';
import { Navigation, Virtual } from 'swiper/modules';

import type { RelatedProductRenderer } from '@zoomit/editor';
import { BreakpointsValues, Flex, Image, Link, Skeleton, Spacing, useMounted } from '@zoomit/primitives';
import type { BaseImageType, ProductListItemType } from '@zoomit/web-kit';
import { isMobile, SwiperBase } from '@zoomit/web-kit';
import { cssVar, elevation, theme, Typography } from '@zoomit/zoomit-ds';

import { useGenerateSingleProductHref } from '../../../hooks/catalog/useGenerateSingleProductHref';
import { useProductPriceQuery } from '../../../hooks/useProductPrice';
import { useOrigin } from '../../../providers/OriginProvider';

type Props = {
  blockData: RelatedProductRenderer;
  blockId: string;
};

const RelatedProductSlider = (props: Props) => {
  const { blockData, blockId } = props;
  const isMounted = useMounted();

  const productsIds = blockData.products.map((e) => e.id);
  const { findPrice, productQuery } = useProductPriceQuery({ productsIds });
  const { data: prices, isLoading } = productQuery;
  const ProductCards = useCallback(
    () =>
      blockData.products.map((product) => (
        <ProductCard
          {...(product as ProductListItemType)}
          key={product.id}
          price={findPrice(product.id, prices)}
          isLoading={isLoading}
        />
      )),
    [isLoading],
  );

  const swiperBreakpoints = {
    [`${BreakpointsValues.xxl}`]: {
      slidesPerView: 4,
    },
    [`${BreakpointsValues.lg}`]: {
      slidesPerView: 3,
    },
  };

  return (
    <Flex gap={isMobile ? 1 : 4} flexWrap={'nowrap'} flexDirection="column" spacing={{ py: 'x4' }}>
      {isMobile ? (
        <MobileTitle variant="h5">{blockData.title}</MobileTitle>
      ) : (
        <DesktopTitle variant="h5" textAlign="center">
          {blockData.title}
        </DesktopTitle>
      )}
      {isMobile ? (
        <MobileArticleWrapper>{ProductCards()}</MobileArticleWrapper>
      ) : (
        <SwiperBase
          modules={[Navigation, Virtual]}
          containerStyle={{ width: '100%', maxWidth: 'calc(100% - 9.4rem)' }}
          slideStyle={{ display: 'grid', placeItems: 'center' }}
          slidesPerView={2}
          breakpoints={swiperBreakpoints}
          spacing={{ mx: 'auto' }}
          grabCursor={true}
          navigation={{
            prevEl: '.swiper-button-prev',
            nextEl: '.swiper-button-next',
          }}
          virtual={
            isMounted
              ? {
                  enabled: true,
                  addSlidesAfter: 2,
                }
              : false
          }
          key={`${blockId}-${isMounted ? 'virtual' : 'normal'}`}
          slides={ProductCards()}
          hasNextArrow={true}
          hasPrevArrow={true}
        />
      )}
    </Flex>
  );
};

export default RelatedProductSlider;

type ProductCardProps = {
  id: string;
  image: BaseImageType;
  title: string;
  isLoading: boolean;
  price: string;
  slug: string;
};

const ProductCard = (props: ProductCardProps) => {
  const { image, title, isLoading, price, slug } = props;

  const { baseMediaUrl } = useOrigin();
  const href = useGenerateSingleProductHref(slug);
  return (
    <Root spacing={{ pt: 'x6', pb: 'x4' }} isMobile={isMobile} className="shrink-0">
      <Link href={href}>
        <Image
          src={`${baseMediaUrl}/${image.fileName}-${image.id}`}
          placeholder="blur"
          blurDataURL={image.preview}
          alt={image.alt}
          width={200}
          height={133}
          style={{ objectFit: 'contain' }}
          sizes="220px"
        />
      </Link>
      <Detail
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        gap={Spacing.x}
        spacing={{ mt: 'x2', px: 'x4' }}
      >
        <Link href={href} className="**:hover:text-text-hover">
          <EllipsisText
            lineNumber={2}
            fontSize={1.2}
            fontWeight="bold"
            lineHeight={2}
            textAlign="center"
            tag="span"
            textColor={cssVar(theme.black3)}
          >
            {title}
          </EllipsisText>
        </Link>
        {isLoading ? (
          <Flex justifyContent={'center'}>
            <Skeleton height={18} width={140} />
          </Flex>
        ) : (
          <Typography variant="subhead" textAlign="center">
            {+price !== 0 && (
              <>
                از
                <Typography spacing={{ mx: 'x' }} className="fa" tag="span" variant="h5">
                  {new Intl.NumberFormat('ir-IR').format(+price)}
                </Typography>
                تومان
              </>
            )}
          </Typography>
        )}
      </Detail>
    </Root>
  );
};

const Root = styled(Flex)<{ isMobile: boolean }>`
  width: 200px;
  border-radius: var(${theme.radius.sm});
  background-color: var(${theme.card});
  position: relative;
  ${({ isMobile }) => isMobile && elevation}
`;

const Detail = styled(Flex)`
  margin-top: 0.8rem;
  padding: 0 1.6rem;
  text-align: center;
  height: 8.4rem;
  margin-inline: auto;
`;

const EllipsisText = styled(Typography)<{ lineNumber: number }>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ lineNumber }) => lineNumber};
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  height: 4.6rem;
`;

const DesktopTitle = styled(Typography)`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1.3rem;
    left: 50%;
    transform: translateX(-50%);
    width: 5rem;
    height: 0.4rem;
    background-color: ${cssVar(theme.primary1)};
    border-radius: var(${theme.radius.full});
  }
`;

const MobileTitle = styled(Typography)`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 2.4rem;
    top: 0;
    right: -1.6rem;
    background-color: ${cssVar(theme.primary1)};
  }
`;

const MobileArticleWrapper = styled(Flex)`
  width: 100%;
  height: 100%;
  gap: 1.6rem;
  overflow: auto;
  padding: 1.6rem 0.2rem;
  flex-wrap: nowrap;
`;
